import { useEffect, useRef } from 'react'

export type Direction = 'up' | 'down' | 'left' | 'right'
export type Position = [number, number]
export type Options = {
  /**
   * CSS className that is applied to the HTMLDivElement when swipping is started and stopped
   */
  swippingClassName?: string

  /**
   * Invoked when onTouchStart & onTouchMove are called
   *
   * If this function returns false, onTouchStart || onTouchMove will not run
   *
   * @returns boolean
   */
  checkCanStartSwipe?: (e: TouchEvent) => boolean
  checkCanContinueSwipe?: (e: TouchEvent) => boolean

  onTouchStart?: () => void
  onTouchMove?: (dragOffset: number, direction: Direction, currentPosition: Position) => void
  onTouchEnd?: (dragOffset: number, endPosition: Position, swipeSpeed: number, direction: Direction) => void
}

export const useSwipeEffects = (
  swipeDirection: 'horizontal' | 'vertical', // Determins whether or not we invoke onTouchMove based on passing our jitter threshold
  options?: Options,
) => {
  const swipeRef = useRef<HTMLDivElement | null>(null)

  const dragDirection = useRef<Direction | null>(null)
  const dragOffset = useRef<Position | null>(null)

  const startingTouchPosition = useRef<Position | null>(null)
  const startingTouchTime = useRef<number | null>(null)

  const axisOfMotion = swipeDirection === 'horizontal' ? 0 : 1
  const staticAxis = swipeDirection === 'horizontal' ? 1 : 0

  const JITTER_THRESHOLD = 25 // Threshold to ignore all swipes until past this point

  const handleTouchStart = (touchEvent: TouchEvent) => {
    if (options?.checkCanStartSwipe && !options.checkCanStartSwipe(touchEvent)) return

    startingTouchPosition.current = [touchEvent.touches[0]?.clientX, touchEvent.touches[0]?.clientY]
    startingTouchTime.current = touchEvent.timeStamp

    if (options?.onTouchStart) options.onTouchStart()
  }

  const handleTouchMove = (touchEvent: TouchEvent) => {
    if (options?.checkCanContinueSwipe && !options.checkCanContinueSwipe(touchEvent)) return handleResetState()

    if (startingTouchPosition.current === null) return

    const currentX = touchEvent.touches[0]?.clientX
    const currentY = touchEvent.touches[0]?.clientY

    const currentOnAxis = swipeDirection === 'horizontal' ? currentX : currentY
    const currentSwipeDirection = getSwipeDirection(startingTouchPosition.current[axisOfMotion], currentOnAxis)

    // If they reverse direction, treat the swipe as if it started at the reverse point
    if (dragDirection.current && dragDirection.current !== currentSwipeDirection) {
      startingTouchPosition.current = [currentX, currentY]
      startingTouchTime.current = touchEvent.timeStamp
    }

    dragOffset.current = [currentX - startingTouchPosition.current[0], startingTouchPosition.current[1] - currentY]

    // If we haven't met our threshold AND haven't started dragging yet.
    if (Math.abs(dragOffset.current[axisOfMotion]) < JITTER_THRESHOLD && !dragDirection.current) return

    // If we've moved too far in the opposite direction, don't treat this as a drag in the other direction
    if (Math.abs(dragOffset.current[staticAxis]) > JITTER_THRESHOLD && !dragDirection.current) return

    // Once we start swiping don't allow them to change directions
    if (!dragDirection.current) dragDirection.current = currentSwipeDirection

    if (options?.swippingClassName) swipeRef.current?.classList.add(options.swippingClassName)
    if (options?.onTouchMove && dragDirection.current)
      options.onTouchMove(
        dragOffset.current[axisOfMotion] + getJitterOffset(),
        dragDirection.current,
        dragOffset.current,
      )
  }

  const handleTouchEnd = (touchEvent: TouchEvent) => {
    if (dragOffset.current === null || startingTouchTime.current === null || !dragDirection.current) return

    // NOTE - absolute value because speed is directionless.
    const swipeSpeed = Math.abs(dragOffset.current[axisOfMotion]) / (touchEvent.timeStamp - startingTouchTime.current)

    if (options?.swippingClassName) swipeRef.current?.classList.remove(options.swippingClassName)
    if (options?.onTouchEnd)
      options.onTouchEnd(
        dragOffset.current[axisOfMotion] + getJitterOffset(),
        dragOffset.current,
        swipeSpeed,
        dragDirection.current,
      )

    handleResetState()
  }

  const handleResetState = () => {
    // Always reset state once the drawer has entirely opened/ shut
    dragOffset.current = null
    startingTouchPosition.current = null
    startingTouchTime.current = null
    dragDirection.current = null
  }

  const getSwipeDirection = (previousPostion: number | null, currentPosition: number | null) => {
    if (previousPostion === null || currentPosition === null) return null

    if (swipeDirection === 'horizontal' && previousPostion > currentPosition) return 'left'
    if (swipeDirection === 'horizontal' && previousPostion < currentPosition) return 'right'

    if (swipeDirection === 'vertical' && previousPostion < currentPosition) return 'down'
    if (swipeDirection === 'vertical' && previousPostion > currentPosition) return 'up'

    return null
  }

  const getJitterOffset = () => {
    if (swipeDirection === 'horizontal' && dragDirection.current === 'right') return -1 * JITTER_THRESHOLD
    if (swipeDirection === 'horizontal' && dragDirection.current === 'left') return JITTER_THRESHOLD

    if (swipeDirection === 'vertical' && dragDirection.current === 'up') return -1 * JITTER_THRESHOLD
    if (swipeDirection === 'vertical' && dragDirection.current === 'down') return JITTER_THRESHOLD

    return 0
  }

  useEffect(() => {
    swipeRef.current?.addEventListener('touchstart', handleTouchStart)
    swipeRef.current?.addEventListener('touchmove', handleTouchMove)
    swipeRef.current?.addEventListener('touchend', handleTouchEnd)

    return () => {
      swipeRef.current?.removeEventListener('touchstart', handleTouchStart)
      swipeRef.current?.removeEventListener('touchmove', handleTouchMove)
      swipeRef.current?.removeEventListener('touchend', handleTouchEnd)
    }
  }, [swipeRef.current, swipeDirection, options])

  return swipeRef
}
